import { useEffect, useState } from 'react';
import Iconify from '../../iconify/index.tsx';
import InfiniteScroll from 'react-infinite-scroller';

import {
  Box,
  Card,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Paper,
  Popover,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { OptionType, PickemLeaderboardData } from '@/@types/api.types.ts';
import { useGetPickemLeaderboard } from '@/api/pickem-leaderboard/getPickemLeaderboard.ts';
import { useGetPickem } from '@/api/pickems/getPickem.ts';
import { UserSelectionButton } from '@/components/pickem-edit/PickemLeaderboardTable/UserSelectionButton.tsx';
import { useAppSearchParams } from '@/hooks/useAppSearchParams.ts';
import { SendPrizeEmailButton } from '@/pages/LeaderboardPage/SendPrizeEmailButton.tsx';

export const PickemLeaderboardTable = () => {
  const { pickemId } = useAppSearchParams();

  const [leaderboardData, setLeaderboardData] =
    useState<PickemLeaderboardData | null>(null);
  const [open, setOpen] = useState(null);
  const [openId, setOpenId] = useState<number | null>(null);
  const [checkedUsers, setCheckedUsers] = useState<number[]>([]);

  const {
    data: pickemLeaderboardData,
    isLoading,
    hasNextPage,
    fetchNextPage,
  } = useGetPickemLeaderboard({
    pickemId,
    offset: 0,
    limit: 50,
  });

  const { data: pickem } = useGetPickem(pickemId);

  const rows = leaderboardData
    ? leaderboardData.leaderboard
    : pickemLeaderboardData?.leaderboard ?? [];

  useEffect(() => {
    if (pickem && pickemLeaderboardData) {
      const answers_leaderboard = pickemLeaderboardData.leaderboard.map(
        (userLeaderboard) => {
          const answers = userLeaderboard.selections.reduce(
            (accum, selection) => {
              const question = pickem.questions.find(
                (item) => item.question_id === selection.question_id
              );
              if (question) {
                if (question.is_tiebreak) {
                  accum.correct_custom_value = question.tiebreak_score;
                  accum.user_custom_value = selection.custom_value;
                }
                const option = question.options.find(
                  (item: OptionType) => item.option_id === selection.option_id
                );
                if (option) {
                  if (!!option.is_correct) {
                    accum.correct++;
                  }
                  if (!option.is_correct) {
                    accum.incorrect++;
                  }
                }
              }
              return accum;
            },
            {
              correct: 0,
              incorrect: 0,
              correct_custom_value: 0,
              user_custom_value: 0,
            }
          );
          return { ...userLeaderboard, answers };
        }
      );
      setLeaderboardData({
        ...leaderboardData,
        leaderboard: answers_leaderboard,
      } as any);
    }
  }, [pickem, pickemLeaderboardData]);

  const handleOpenMenu = (event: any) => {
    setOpen(event.currentTarget);
    setOpenId(event.currentTarget.id);
  };

  const handleCloseMenu = () => {
    setOpen(null);
    setOpenId(null);
  };

  const handleCheckUser = (e: any, user_id: number) => {
    if (e.target.checked) {
      checkedUsers.push(user_id);
      setCheckedUsers([...checkedUsers]);
    } else {
      const newCheckedUsers = checkedUsers.filter((item) => item !== user_id);
      setCheckedUsers([...newCheckedUsers]);
    }
  };

  return (
    <>
      {checkedUsers.length > 0 && (
        <Card style={{ padding: 20 }}>
          <Box>
            <Typography variant='h5' component='div' sx={{ marginBottom: 2 }}>
              Message
            </Typography>
            <SendPrizeEmailButton />
          </Box>
        </Card>
      )}
      <Card>
        {isLoading ? (
          <Box
            sx={{ display: 'flex', justifyContent: 'center' }}
            style={{ padding: 20 }}
          >
            <CircularProgress />
          </Box>
        ) : !rows.length ? (
          <Box
            sx={{ display: 'flex', justifyContent: 'center' }}
            style={{ padding: 20 }}
          >
            Leaderboard is empty
          </Box>
        ) : (
          <InfiniteScroll
            pageStart={0}
            loadMore={() => fetchNextPage()}
            hasMore={hasNextPage}
            loader={<Skeleton height='70px' />}
          >
            <TableContainer component={Paper}>
              <Table sx={{ minWidth: 650 }} aria-label='simple table'>
                <TableHead>
                  <TableRow>
                    <TableCell></TableCell>
                    <TableCell>Rank</TableCell>
                    <TableCell>User Id</TableCell>
                    <TableCell>SSO User Id</TableCell>
                    <TableCell>Username</TableCell>
                    <TableCell>Points</TableCell>
                    {pickem && (
                      <>
                        <TableCell>Correct Answers</TableCell>
                        <TableCell>Incorrect Answers</TableCell>
                        <TableCell>TieBreaker</TableCell>
                      </>
                    )}
                    <TableCell>Prize Email</TableCell>
                    <TableCell align='right'></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        '&:last-child td, &:last-child th': { border: 0 },
                      }}
                    >
                      <TableCell component='th' scope='row'>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={checkedUsers.includes(row.user_id)}
                              onChange={(e) => handleCheckUser(e, row.user_id)}
                            />
                          }
                          label=''
                        />
                      </TableCell>
                      <TableCell component='th' scope='row'>
                        {row.rank}
                      </TableCell>
                      <TableCell component='th' scope='row'>
                        {row.user_id}
                      </TableCell>
                      <TableCell component='th' scope='row'>
                        {row.user.sso_user_id}
                      </TableCell>
                      <TableCell component='th' scope='row'>
                        {row.username}
                      </TableCell>
                      <TableCell component='th' scope='row'>
                        {row.points}
                      </TableCell>
                      {pickem && (
                        <>
                          <TableCell component='th' scope='row'>
                            {row.answers?.correct || 0}
                          </TableCell>
                          <TableCell component='th' scope='row'>
                            {row.answers?.incorrect || 0}
                          </TableCell>
                          <TableCell component='th' scope='row'>
                            {`${row.answers?.user_custom_value}(${row.answers?.correct_custom_value})`}
                          </TableCell>
                        </>
                      )}
                      <TableCell component='th' scope='row'>
                        {row?.has_prize_email_sent ? (
                          <Iconify
                            icon={'eva:checkmark-circle-2-outline'}
                            sx={{ color: 'green' }}
                          />
                        ) : (
                          <Iconify
                            icon={'eva:close-circle-outline'}
                            sx={{ color: 'red' }}
                          />
                        )}
                      </TableCell>
                      <TableCell component='th' scope='row' align='right'>
                        <IconButton
                          size='large'
                          color='inherit'
                          onClick={handleOpenMenu}
                          id={String(row.user_id)}
                        >
                          <Iconify icon={'eva:more-vertical-fill'} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </InfiniteScroll>
        )}
      </Card>

      <Popover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            p: 1,
            width: 200,
            '& .MuiMenuItem-root': {
              px: 1,
              typography: 'body2',
              borderRadius: 0.75,
            },
          },
        }}
      >
        <UserSelectionButton pickemId={pickemId} userId={openId} />
      </Popover>
    </>
  );
};
